// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { classNames, numberToFixed } from '@shared/ui-kit/utils';
import { createColumnHelper } from '@tanstack/react-table';
import {
  AppIcon,
  AssetBrand,
  DeleteButton,
  CellData,
  WatchlistToggleButton,
  Table,
} from '@shared/ui-kit/components';
import { type ColumnMethods } from '@advisor-app/features/trades';
import { formatToMoney } from '@advisor-app/features/utils/format-to-money';
import { type PositionsAnalysis } from '@shared/data-access/interfaces/redux/portfolio-state';
import {
  OperationType,
  normalizeOperationType,
} from '@shared/data-access/interfaces/operation-type';
import {
  getTickerValues,
  getIsinValues,
  hasAccountAssociation,
} from '../utils/rebalance-helpers';
import { isCashTicker } from '../utils/trade-helpers';

const columnHelper = createColumnHelper<PositionsAnalysis>();

const iconConfig = {
  [OperationType.BUY]: { icon: 'Trader', color: 'success' },
  [OperationType.SELL]: { icon: 'TrendingDown', color: 'danger' },
  [OperationType.TRANSFER]: { icon: 'ArrowLeftRight', color: 'main' },
} as const;

export const renderColumnsTradesRebalance = (columnMethods: ColumnMethods) => {
  const { tickerMethod: handleDeleteOption, hasOnlyOneData } = columnMethods;

  const columns = [
    // Operation
    columnHelper.accessor('operation', {
      header: 'Operation',
      cell: ({ getValue, row }) => {
        const operation = getValue();
        const normalizedOperation = normalizeOperationType(operation);
        const config =
          iconConfig[normalizedOperation as keyof typeof iconConfig] ??
          iconConfig[OperationType.SELL];
        const { icon, color } = config;
        return (
          <div
            className="flex items-center justify-center gap-x-2"
            data-testid="rebalance-operation"
          >
            <AppIcon icon={icon} color={color} className="inline-block" size="lg" />
            <CellData value={normalizedOperation} format="capitalized" />
          </div>
        );
      },
    }),

    // From Product (Ticker/ISIN in 2 lines)
    columnHelper.accessor('operation', {
      id: 'from_product',
      header: 'From Product',
      minSize: 180,
      cell: ({ row }) => {
        const { from_ticker, from_isin } = {
          ...getTickerValues(row.original),
          ...getIsinValues(row.original),
        };
        const position = row.original;

        // For BUY operations: show "Cash Account" above and currency below
        const normalizedOperation = normalizeOperationType(position.operation);
        if (normalizedOperation === OperationType.BUY) {
          return (
            <div className="flex flex-col">
              <span>Cash Account</span>
              {position.currency && (
                <span className="text-sm">{position.currency}</span>
              )}
            </div>
          );
        }

        if (
          [OperationType.SELL, OperationType.TRANSFER].includes(
            normalizedOperation,
          ) &&
          from_ticker &&
          !isCashTicker(from_ticker) &&
          from_ticker !== position.currency
        ) {
          const positionAny = position as PositionsAnalysis & {
            from_asset_details?: {
              asset_type?: string;
              url_logo?: string;
              url_morningstar?: string;
            };
          };
          const fromAssetDetails = positionAny.from_asset_details;

          const assetType =
            normalizedOperation === OperationType.TRANSFER && fromAssetDetails
              ? fromAssetDetails.asset_type
              : position.asset_type;
          const logoUrl =
            normalizedOperation === OperationType.TRANSFER && fromAssetDetails
              ? fromAssetDetails.url_logo
              : position.url_logo;
          const url =
            normalizedOperation === OperationType.TRANSFER && fromAssetDetails
              ? fromAssetDetails.url_morningstar
              : position.url_morningstar;

          return (
            <AssetBrand
              ticker={from_ticker}
              assetType={assetType}
              logoUrl={logoUrl}
              url={url}
              isin={from_isin}
            />
          );
        }

        if (!from_ticker && !from_isin) {
          return <span>-</span>;
        }

        return (
          <div className="flex flex-col">
            <span>{from_ticker || '-'}</span>
            {from_isin && from_isin !== from_ticker && (
              <span className="text-sm">{from_isin}</span>
            )}
          </div>
        );
      },
    }),

    // From Account
    columnHelper.accessor('operation', {
      id: 'from_account',
      header: 'From Account',
      cell: ({ row }) => {
        const position = row.original;
        const institutionName = position.institution_name;
        const accountId = position.account_id;
        const normalizedOperation = normalizeOperationType(position.operation);

        // For BUY operations: show "Nuvara Inversis" above and "Cash Account" below
        if (normalizedOperation === OperationType.BUY) {
          return (
            <div className="flex flex-col">
              <span>Nuvara Inversis</span>
              <span className="text-sm">Cash Account</span>
            </div>
          );
        }

        // For TRANSFER: if no data, show "Nuvara Inversis" above and "Investment Account" below
        if (normalizedOperation === OperationType.TRANSFER) {
          if (!institutionName && !accountId) {
            return (
              <div className="flex flex-col">
                <span>Nuvara Inversis</span>
                <span className="text-sm">Investment Account</span>
              </div>
            );
          }
        }

        // If both values are missing for SELL, show only one "-"
        if (!institutionName && !accountId) {
          return <span>-</span>;
        }

        // Show institution name above and account_id below
        return (
          <div className="flex flex-col">
            {institutionName && <span>{institutionName}</span>}
            {accountId && (
              <span className={institutionName ? 'text-sm' : ''}>{accountId}</span>
            )}
          </div>
        );
      },
    }),

    // Investment
    columnHelper.accessor('volume', {
      header: 'Investment',
      meta: { alignment: 'end' },
      cell: ({ getValue, row }) => {
        const value = getValue();
        const currency = row.original.currency;

        // If both value and currency are missing, show only one "-"
        if (!value && !currency) {
          return <span>-</span>;
        }

        return (
          <div className="flex flex-col items-end">
            <span className="!money-display">
              {value ? formatToMoney(value, currency) : '-'}
            </span>
            {currency && <span className="text-sm">{currency}</span>}
          </div>
        );
      },
    }),

    // Gain / Loss
    columnHelper.accessor('realized_capital_gains', {
      header: 'Gain / Loss',
      id: 'gain_loss',
      meta: {
        alignment: 'end',
      },
      sortingFn: 'gainLoss',
      cell: ({ getValue, row }) => {
        const value = getValue();
        const volume = row.original.volume;
        const currency = row.original.currency;
        const percentage = volume ? numberToFixed((value / volume) * 100) : 0;

        return (
          <span
            data-testid="rebalance-gain-loss"
            className={classNames({
              profit: value >= 0,
              loss: value < 0,
            })}
          >
            <Table.Data value={value} format="currency" currency={currency} />
            &nbsp;(
            <Table.Data value={percentage} format="percentage" />)
          </span>
        );
      },
    }),

    // To Product (Ticker/ISIN in 2 lines)
    columnHelper.accessor('operation', {
      id: 'to_product',
      header: 'To Product',
      minSize: 180,
      enableHiding: false,
      cell: ({ row }) => {
        const { to_ticker, to_isin } = {
          ...getTickerValues(row.original),
          ...getIsinValues(row.original),
        };
        const position = row.original;

        // For SELL operations: show "Cash Account" above and currency below
        const normalizedOperation = normalizeOperationType(position.operation);
        if (normalizedOperation === OperationType.SELL) {
          return (
            <div className="flex flex-col">
              <span>Cash Account</span>
              {position.currency && (
                <span className="text-sm">{position.currency}</span>
              )}
            </div>
          );
        }

        if (
          [OperationType.BUY, OperationType.TRANSFER].includes(
            normalizedOperation,
          ) &&
          to_ticker &&
          !isCashTicker(to_ticker) &&
          to_ticker !== position.currency
        ) {
          return (
            <AssetBrand
              ticker={to_ticker}
              assetType={position.asset_type}
              logoUrl={position.url_logo}
              url={position.url_morningstar}
              watchlistButton={
                <WatchlistToggleButton
                  ticker={to_ticker}
                  assetType={position.asset_type as string}
                />
              }
              isin={to_isin}
            />
          );
        }

        if (!to_ticker && !to_isin) {
          return <span>-</span>;
        }

        return (
          <div className="flex flex-col">
            <span>{to_ticker || '-'}</span>
            {to_isin && to_isin !== to_ticker && (
              <span className="text-sm">{to_isin}</span>
            )}
          </div>
        );
      },
    }),

    // To Account
    columnHelper.accessor('operation', {
      id: 'to_account',
      header: 'To Account',
      cell: ({ row }) => {
        const position = row.original;
        const institutionName = position.institution_name;
        const accountId = position.account_id;
        const normalizedOperation = normalizeOperationType(position.operation);

        // For BUY and TRANSFER: show "Nuvara Inversis" above and "Investment Account" below
        if (
          [OperationType.BUY, OperationType.TRANSFER].includes(normalizedOperation)
        ) {
          return (
            <div className="flex flex-col">
              <span>Nuvara Inversis</span>
              <span className="text-sm">Investment Account</span>
            </div>
          );
        }

        // For SELL: show institution name above and account_id below
        // Only show if trade has account association
        if (normalizedOperation === OperationType.SELL) {
          if (!hasAccountAssociation(position)) {
            return <span>-</span>;
          }

          return (
            <div className="flex flex-col">
              {institutionName && <span>{institutionName}</span>}
              {accountId && (
                <span className={institutionName ? 'text-sm' : ''}>
                  {accountId}
                </span>
              )}
            </div>
          );
        }

        // If both values are missing, show only one "-"
        if (!institutionName && !accountId) {
          return <span>-</span>;
        }

        // Show institution name above and account_id below
        return (
          <div className="flex flex-col">
            {institutionName && <span>{institutionName}</span>}
            {accountId && (
              <span className={institutionName ? 'text-sm' : ''}>{accountId}</span>
            )}
          </div>
        );
      },
    }),

    // Options column
    columnHelper.accessor('operation', {
      id: 'options',
      enableSorting: false,
      meta: { showColumnVisibilitySelector: true },
      header: '',
      cell: ({ getValue, row }) => (
        <div className="flex space-x-2">
          <DeleteButton
            className={classNames({ '!hidden': hasOnlyOneData })}
            data-testid="delete-rebalance-asset"
            onClick={() => handleDeleteOption?.(row.original.ticker, row.index)}
          />
        </div>
      ),
    }),
  ];

  return columns;
};
